import clsx from 'clsx';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { toast } from 'react-toastify';

import { useGetUser } from '@hooks/apiHooks/userHooks';
import {
  useGetKnowledgeBase,
  useTwinPersonaById,
  useUploadMedia,
} from '../../hooks/apiHooks/twinHooks';

import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useVisualViewportHeight } from '@hooks/useVisualViewportHeight';
import { IUserData } from 'src/api/user';
import { useTwinData } from '../../hooks/storeHooks/useTwin';
import { ConsentTab } from './components/ConsentTab/ConsentTab';
import { FaqTab } from './components/FaqTab';
import { InterviewTab } from './components/InterviewTab';
import { LinkedInTab } from './components/LinkedInTab';
import { MainTab } from './components/MainTab';
import { Privacy } from './components/Privacy';
import { ProfileTab } from './components/ProfileTab';
import { PublishTab } from './components/PublishTab';
import { SelfieTab } from './components/SelfieTab';
import { TabNav } from './components/TabNav';
import { useTabs } from './hooks/useTabs';

const TwinPage: React.FC = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    jobTitle,
    twinId,
    profileJson,
    setAudioUrl,
    resetTwinState,
    setKnowledge,
    isCameraReady,
    localTwinIsReady,
  } = useTwinData();

  const { data: userData, refetch: refetchUser } = useGetUser();

  const { user, isLoading: isAuthLoading } = useAuth();
  const { sessionId } = useSessionFeatureState();
  useVisualViewportHeight();

  const tabRoutes = useMemo(
    () => [
      { label: t('twin.tabLinkedIn'), path: '/linkedin' },
      { label: t('twin.tabSelfie'), path: '/selfie' },
      { label: t('twin.tabInterview'), path: '/interview' },
      { label: t('twin.tabFaq'), path: '/knowledge/files' },
      { label: t('twin.tabProfile'), path: '/info' },
      { label: t('twin.tabConsent'), path: '/consent' },
      { label: t('twin.tabReady'), path: '/publish' },
    ],
    [t],
  );

  const storedUser = useMemo(() => {
    return userData?.data;
  }, [userData]);

  const { data, isLoading, isFetching } = useTwinPersonaById(
    storedUser?.mentorId || '',
    storedUser?.mentorId !== null,
    false,
  );

  const mentorStatus = useMemo<string | null>(
    () => data?.data?.profile?.profile?.status as string,
    [data],
  );

  const twinImageUrl = useMemo(() => {
    return data?.data?.profile?.profile?.profilePictureUrl;
  }, [data]);

  const [interviewRestartKey, setInterviewRestartKey] = useState(0);
  const { activeIndex, handleChange } = useTabs(tabRoutes, '/twin');

  const { mutateAsync: uploadMedia } = useUploadMedia();
  const {
    mutateAsync: fetchKnowledgeBase,
    isPending: isFetchingKnowledgeBase,
  } = useGetKnowledgeBase();

  const openNextTab = () => {
    window.scrollTo(0, 0);
    handleChange(activeIndex + 1);
  };

  // this need to be in parent component because we do it in background
  const handleAudioProcessing = async (audioBlob: Blob) => {
    console.log('Starting background audio processing...');

    openNextTab();

    uploadMedia({
      file: audioBlob,
      type: 'audio',
      name: `${Date.now()}-voice-recording.wav`,
    })
      .then((audioUrl) => {
        console.log('Audio uploaded successfully:', audioUrl);
        if (audioUrl) {
          setAudioUrl(audioUrl);
        }
      })
      .catch((error) => {
        console.error('Error uploading audio:', error);
        toast.error(t('twin.audioUploadFailed'));
      });

    if (sessionId) {
      console.log('Fetching knowledge base...');
      fetchKnowledgeBase({
        avatarSessionId: sessionId,
        linkedinProfile: profileJson || '',
      })
        .then(({ knowledge, job_title }) => {
          setKnowledge({
            knowledge: knowledge,
            jobTitle: job_title,
          });
        })
        .catch((error) => {
          console.error('Error fetching knowledge base:', error);
          toast.error(t('twin.knowledgeFetchFailed'));
        });
    }
  };

  const handleInterviewRestart = () => {
    setInterviewRestartKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (mentorStatus && mentorStatus !== 'Completed') {
      navigate(`/twin/publish${location.search}`, { replace: true });
    }
  }, [mentorStatus]);

  useEffect(() => {
    navigate(`/twin${location.search}`, { replace: true });
    return () => {
      resetTwinState();
    };
  }, []);

  const showNav = useMemo(() => {
    return (
      activeIndex !== -1 &&
      (!location.pathname.includes('/knowledge') ||
        mentorStatus !== 'Completed')
    );
  }, [activeIndex, mentorStatus, location.pathname, mentorStatus]);

  return (
    <>
      <div className="!fixed !w-screen !h-[var(--vh,100vh)] !left-0 !top-[var(--vh-offset,0px)] bg-black text-left opacity-100 !z-[2000] overflow-y-auto overflow-x-hidden">
        <div
          className={clsx(
            'min-h-[var(--vh,100vh)] overflow-hidden px-4 py-8',
            'flex flex-col justify-start items-center',
            'font-dmsans text-white',
            "bg-[url('/assets/images/twin-bg.jpeg')] bg-cover bg-center",
            'md:px-24',
            'min-1100:w-screen min-1100:justify-center',
            'sm:!min-h-[var(--vh,100vh)]',
            {
              'sm:relative fixed': isCameraReady,
            },
          )}>
          {/* Mobile/tablet: the logo sits in normal flow at the top and scrolls
              away with the content, so it can never overlap content on scroll.
              At min-1100 it returns to a fixed top-left position over the
              vertically-centered desktop layout (unchanged). */}
          <Link
            to="/companions"
            className="self-start mb-6 h-8 w-auto opacity-100 z-[99999] min-1100:fixed min-1100:top-[28px] min-1100:left-6 min-1100:mb-0">
            <img
              src="/assets/images/logo_napster_white.svg"
              alt="Napster"
              className="h-8"
            />
          </Link>
          {showNav && !localTwinIsReady && (
            <TabNav tabRoutes={tabRoutes} activeIndex={activeIndex} />
          )}
          {activeIndex === -1 && (
            <MainTab
              user={
                {
                  ...storedUser,
                  mentorSlug: data?.data?.profile?.profile?.slug || '',
                } as IUserData
              }
              locationSearch={location.search}
              isLoading={isLoading || isFetching || isAuthLoading || !storedUser}
              isReadyStatus={
                mentorStatus
                  ? mentorStatus === 'ReadyToUse' ||
                    mentorStatus === 'Completed'
                  : false
              }
              twinImageUrl={twinImageUrl || ''}
              openNextTab={openNextTab}
              refetchUser={refetchUser}
            />
          )}
          <Routes>
            <Route
              path="/linkedin"
              element={<LinkedInTab openNextTab={openNextTab} />}
            />
            <Route
              path="/selfie"
              element={<SelfieTab openNextTab={openNextTab} />}
            />
            <Route
              path="/interview"
              element={
                <InterviewTab
                  key={interviewRestartKey}
                  setAudioBlob={handleAudioProcessing}
                  onRestart={handleInterviewRestart}
                />
              }
            />
            <Route
              path="/knowledge/*"
              element={
                <FaqTab
                  openNextTab={openNextTab}
                  mentorId={storedUser?.mentorId || ''}
                  localTwinIsReady={localTwinIsReady}
                  isReadyStatus={
                    mentorStatus
                      ? mentorStatus === 'ReadyToUse' ||
                        mentorStatus === 'Completed'
                      : false
                  }
                />
              }
            />
            {/* <Route path="/faq" element={<FaqTabPage openNextTab={openNextTab} />} /> */}

            <Route
              path="/info"
              element={
                <ProfileTab
                  userData={{
                    firstName: (storedUser?.firstName as string) || '',
                    lastName: (storedUser?.lastName as string) || '',
                    jobTitle:
                      jobTitle || (user?.profile.jobTitle as string) || '',
                    title: (user?.profile.title as string) || '',
                  }}
                  isFetchingKnowledge={isFetchingKnowledgeBase}
                  openNextTab={openNextTab}
                />
              }
            />
            <Route
              path="/consent"
              element={<ConsentTab openNextTab={openNextTab} />}
            />
            <Route
              path="/publish"
              element={
                <PublishTab
                  twinId={twinId || data?.data?.profile?.profile?.id || ''}
                  locationSearch={location.search}
                  refetchUser={refetchUser}
                />
              }
            />
          </Routes>

          {activeIndex !== -1 && <Privacy />}
        </div>
      </div>
    </>
  );
});

export { TwinPage };
